/**
 * useImageGeneratorAI - Defines ArcheAICapabilities for the Image Generator arche.
 *
 * Bridges the image generator's state management with Fias AI,
 * allowing the AI to read panel state and propose configuration changes.
 */

import { useMemo, useCallback } from 'react';
import type { ArcheAICapabilities, ArcheAIAction } from '@/components/fias-ai/types';
import type { ImageGeneratorState } from '../hooks/useImageGeneratorState';
import type { ImagePreset, ReferenceImage } from '../types/image-generator.types';
import {
  NUM_INSTANCES,
  ARCHE_ID,
  FALLBACK_SIZE_OPTIONS,
  FALLBACK_QUALITY_OPTIONS,
  FALLBACK_STYLE_OPTIONS,
  ENHANCEMENT_CATEGORIES,
} from '../types/image-generator.types';

interface UseImageGeneratorAIOptions {
  state: ImageGeneratorState;
  updateInstanceConfig: (idx: number, updates: Record<string, unknown>) => void;
  toggleInstance: (idx: number) => void;
  toggleMinimized: (idx: number) => void;
  applyPresetToInstance: (idx: number, preset: ImagePreset | null) => void;
  setReferenceImage: (idx: number, ref: ReferenceImage) => void;
  clearReferenceImage: (idx: number) => void;
  availableModels: Array<{
    modelName: string;
    displayName: string;
    provider: string;
    capabilities: Record<string, unknown>;
  }>;
}

const IMAGE_GENERATOR_ACTIONS: ArcheAIAction[] = [
  {
    id: 'set_model',
    description:
      'Set the AI model for a specific panel. Use this to change which model generates images.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
        modelName: {
          type: 'string',
          description: 'Model name to set (e.g., "gpt-image-1", "flux-1.1-pro")',
        },
      },
      required: ['panelNumber', 'modelName'],
    },
    requiresConfirmation: false,
    category: 'settings',
  },
  {
    id: 'set_size',
    description: 'Set the image aspect ratio/size for a specific panel.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
        size: {
          type: 'string',
          description: 'Aspect ratio string (e.g., "1:1", "16:9", "9:16", "4:3")',
        },
      },
      required: ['panelNumber', 'size'],
    },
    requiresConfirmation: false,
    category: 'settings',
  },
  {
    id: 'set_quality',
    description: 'Set the image quality level for a specific panel.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
        quality: {
          type: 'string',
          description: 'Quality level (e.g., "standard", "hd")',
        },
      },
      required: ['panelNumber', 'quality'],
    },
    requiresConfirmation: false,
    category: 'settings',
  },
  {
    id: 'set_style',
    description: 'Set the image style for a specific panel.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
        style: {
          type: 'string',
          description: 'Style value (e.g., "vivid", "natural")',
        },
      },
      required: ['panelNumber', 'style'],
    },
    requiresConfirmation: false,
    category: 'settings',
  },
  {
    id: 'toggle_panel',
    description:
      'Enable or disable a panel. Enabling a disabled panel makes it active for image generation.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
      },
      required: ['panelNumber'],
    },
    requiresConfirmation: false,
    category: 'layout',
  },
  {
    id: 'toggle_panel_minimize',
    description:
      'Expand or collapse a panel. Minimized panels are still enabled but take less space.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
      },
      required: ['panelNumber'],
    },
    requiresConfirmation: false,
    category: 'layout',
  },
  {
    id: 'set_enhancement',
    description:
      'Set a prompt enhancement value for a panel. Enhancements are appended to the user prompt.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
        category: {
          type: 'string',
          description:
            'Enhancement category: subjectDetails, composition, lighting, styleMedium, background, mood, colorTone, or technicalQuality',
        },
        value: {
          type: 'string',
          description: 'Enhancement text value',
        },
      },
      required: ['panelNumber', 'category', 'value'],
    },
    requiresConfirmation: false,
    category: 'settings',
  },
  {
    id: 'apply_preset',
    description:
      'Apply a saved preset to a panel. Presets bundle model, size, quality, style, and enhancement settings.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
        presetName: {
          type: 'string',
          description: 'Name of the preset to apply',
        },
      },
      required: ['panelNumber', 'presetName'],
    },
    requiresConfirmation: false,
    category: 'settings',
  },
  {
    id: 'configure_all_panels',
    description:
      'Set the same configuration on all enabled panels at once. Only specified fields are changed.',
    parameterSchema: {
      type: 'object',
      properties: {
        modelName: {
          type: 'string',
          description: 'Model name to set on all panels',
        },
        size: {
          type: 'string',
          description: 'Aspect ratio to set on all panels',
        },
        quality: {
          type: 'string',
          description: 'Quality level to set on all panels',
        },
        style: {
          type: 'string',
          description: 'Style to set on all panels',
        },
      },
    },
    requiresConfirmation: false,
    category: 'settings',
  },
  {
    id: 'set_reference_image',
    description:
      'Set a previously generated image as reference for image-to-image generation on a panel. The imageRef must come from a recently generated image in that panel.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
        imageRef: {
          type: 'string',
          description: 'The imageRef of a generated image to use as reference',
        },
      },
      required: ['panelNumber', 'imageRef'],
    },
    requiresConfirmation: false,
    category: 'settings',
  },
  {
    id: 'clear_reference_image',
    description: 'Remove the reference image from a panel, reverting to text-to-image generation.',
    parameterSchema: {
      type: 'object',
      properties: {
        panelNumber: {
          type: 'number',
          description: 'Panel number (1-5)',
        },
      },
      required: ['panelNumber'],
    },
    requiresConfirmation: false,
    category: 'settings',
  },
];

export function useImageGeneratorAI({
  state,
  updateInstanceConfig,
  toggleInstance,
  toggleMinimized,
  applyPresetToInstance,
  setReferenceImage,
  clearReferenceImage,
  availableModels,
}: UseImageGeneratorAIOptions): ArcheAICapabilities {
  const getStateSnapshot = useCallback((): Record<string, unknown> => {
    const panels = Array.from({ length: NUM_INSTANCES }, (_, idx) => {
      const config = state.instanceConfigs[idx];
      const enabled = state.enabledInstances[idx];
      const minimized = state.minimizedInstances[idx];

      if (!enabled) {
        return { panelNumber: idx + 1, enabled: false };
      }

      // Include last 5 images for AI reference
      const images = state.instanceImages[idx] || [];
      const recentImages = images.slice(-5).map((img) => ({
        imageRef: img.imageRef,
        prompt: img.prompt.length > 80 ? img.prompt.substring(0, 80) + '...' : img.prompt,
      }));

      return {
        panelNumber: idx + 1,
        enabled: true,
        minimized,
        model: config.modelName,
        provider: config.provider,
        size: config.size,
        quality: config.quality,
        style: config.style,
        presetName: config.presetName,
        enhancements: Object.fromEntries(
          Object.entries(config.enhancements).filter(([, v]) => v !== ''),
        ),
        referenceImage: config.referenceImage
          ? { imageRef: config.referenceImage.imageRef, source: config.referenceImage.source }
          : null,
        recentImages: recentImages.length > 0 ? recentImages : undefined,
      };
    });

    return {
      panels,
      availableModels: availableModels.map((m) => ({
        modelName: m.modelName,
        displayName: m.displayName,
        provider: m.provider,
      })),
      availablePresets: state.presets.map((p) => p.name),
      availableSizes: FALLBACK_SIZE_OPTIONS.map((s) => s.value),
      availableQualities: FALLBACK_QUALITY_OPTIONS.map((q) => q.value),
      availableStyles: FALLBACK_STYLE_OPTIONS.map((s) => s.value),
      enhancementCategories: ENHANCEMENT_CATEGORIES.map((c) => ({
        key: c.key,
        label: c.label,
        description: c.description,
      })),
    };
  }, [state, availableModels]);

  const applyAction = useCallback(
    (actionId: string, params: Record<string, unknown>) => {
      // Convert 1-based panelNumber (user-facing) to 0-based index (internal)
      const panelNumber = params.panelNumber as number | undefined;
      const panelIndex = panelNumber != null ? panelNumber - 1 : undefined;

      switch (actionId) {
        case 'set_model': {
          if (panelIndex == null) return;
          const model = availableModels.find((m) => m.modelName === params.modelName);
          if (model) {
            updateInstanceConfig(panelIndex, {
              modelName: model.modelName,
              provider: model.provider,
            });
          }
          break;
        }
        case 'set_size': {
          if (panelIndex == null) return;
          updateInstanceConfig(panelIndex, { size: params.size });
          break;
        }
        case 'set_quality': {
          if (panelIndex == null) return;
          updateInstanceConfig(panelIndex, { quality: params.quality });
          break;
        }
        case 'set_style': {
          if (panelIndex == null) return;
          updateInstanceConfig(panelIndex, { style: params.style });
          break;
        }
        case 'toggle_panel': {
          if (panelIndex == null) return;
          toggleInstance(panelIndex);
          break;
        }
        case 'toggle_panel_minimize': {
          if (panelIndex == null) return;
          toggleMinimized(panelIndex);
          break;
        }
        case 'set_enhancement': {
          if (panelIndex == null) return;
          const category = params.category as string;
          const value = params.value as string;
          if (category && value) {
            const config = state.instanceConfigs[panelIndex];
            if (config) {
              updateInstanceConfig(panelIndex, {
                enhancements: { ...config.enhancements, [category]: value },
              });
            }
          }
          break;
        }
        case 'apply_preset': {
          if (panelIndex == null) return;
          const presetName = params.presetName as string;
          const preset = state.presets.find((p) => p.name === presetName);
          if (preset) {
            applyPresetToInstance(panelIndex, preset);
          }
          break;
        }
        case 'configure_all_panels': {
          const updates: Record<string, unknown> = {};
          if (params.modelName) {
            const model = availableModels.find((m) => m.modelName === params.modelName);
            if (model) {
              updates.modelName = model.modelName;
              updates.provider = model.provider;
            }
          }
          if (params.size) updates.size = params.size;
          if (params.quality) updates.quality = params.quality;
          if (params.style) updates.style = params.style;

          if (Object.keys(updates).length > 0) {
            for (let i = 0; i < NUM_INSTANCES; i++) {
              if (state.enabledInstances[i]) {
                updateInstanceConfig(i, updates);
              }
            }
          }
          break;
        }
        case 'set_reference_image': {
          if (panelIndex == null) return;
          const imageRef = params.imageRef as string;
          if (!imageRef) return;
          // Find the image in the panel's gallery to get the thumbnail URL
          const images = state.instanceImages[panelIndex] || [];
          const img = images.find((i) => i.imageRef === imageRef);
          setReferenceImage(panelIndex, {
            imageRef,
            thumbnailUrl: img?.imageUrl || '',
            source: 'generated',
          });
          break;
        }
        case 'clear_reference_image': {
          if (panelIndex == null) return;
          clearReferenceImage(panelIndex);
          break;
        }
      }
    },
    [
      state,
      availableModels,
      updateInstanceConfig,
      toggleInstance,
      toggleMinimized,
      applyPresetToInstance,
      setReferenceImage,
      clearReferenceImage,
    ],
  );

  return useMemo(
    () => ({
      archeId: ARCHE_ID,
      enabled: true,
      archeDescription:
        'Image Generator arche with up to 5 simultaneous AI image generation panels. ' +
        'Each panel has independent settings for model, size, quality, style, and prompt enhancements. ' +
        'Users may refer to the current panel configurations as "presets" or "settings" interchangeably — ' +
        'these mean the active panel settings, not the saved presets list.',
      actions: IMAGE_GENERATOR_ACTIONS,
      getStateSnapshot,
      applyAction,
    }),
    [getStateSnapshot, applyAction],
  );
}
